/**
 * GlobalAssistantDock — the always-present assistant entry point (design doc §6).
 *
 * Rendered once at the app root (inside HelpProvider, only when authenticated),
 * so it floats over EVERY authed route — the tabs AND any pushed detail screen.
 *
 * Collapsed: a FAB/pill with action-forward copy "Ask BEAM — I'll do it for you"
 * (not "type a command"). Tapping expands the input sheet.
 *
 * Expanded: reuses the shared `Field` + `MicButton` (edge STT, invariant 5) + Send.
 * Placeholder is screen-aware via AssistantDockProvider. On submit it calls
 * assistantAct(message, { route }) and handles every result `kind`:
 *   - action        → success banner with `summary`, then navigate (handled by the hook).
 *   - answer        → show `content` inline with an "Open full assistant" affordance.
 *   - clarification → show `clarification`, keep the dock open for the follow-up turn.
 *   - needs_owner   → show `reason` + that an owner must approve.
 * Loading + error states are rendered. Never a no-op (CLAUDE.md invariant 7).
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Keyboard,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { AssistantActionResult, AssistantTurn } from '../../api/assistant';
import { Button, Card, Field, Text } from '../ui';
import { MicButton } from '../voice/MicButton';
import { theme } from '../../theme';
import { useAssistantDock } from './AssistantDockProvider';
import { useAssistantAction } from './useAssistantAction';

const COLLAPSED_LABEL = 'Ask me';

/** A rendered reply that is NOT a navigation (answer / clarification / needs_owner). */
type DockReply =
  | { kind: 'answer'; summary: string; content: string }
  | { kind: 'clarification'; clarification: string; progress?: { step: number; total: number } }
  | { kind: 'needs_owner'; summary: string; reason: string };

function toReply(result: AssistantActionResult): DockReply | null {
  switch (result.kind) {
    case 'answer':
      return { kind: 'answer', summary: result.summary, content: result.content ?? result.summary };
    case 'clarification':
      return { kind: 'clarification', clarification: result.clarification ?? result.summary, ...(result.progress ? { progress: result.progress } : {}) };
    case 'needs_owner':
      return { kind: 'needs_owner', summary: result.summary, reason: result.reason ?? result.summary };
    default:
      return null; // 'action' → navigation, no inline reply
  }
}

/** Join the committed field text with the in-progress dictation partial (space-joined). */
function composeInput(committed: string, partial: string): string {
  if (!partial) return committed;
  return committed.trim() ? `${committed} ${partial}` : partial;
}

/** The assistant's turn text to record in history (so a follow-up keeps context). */
function assistantTurnContent(result: AssistantActionResult): string {
  switch (result.kind) {
    case 'action':
      return `✓ ${result.summary}`;
    case 'answer':
      return result.content ?? result.summary;
    case 'clarification':
      return result.clarification ?? result.summary;
    case 'needs_owner':
      return result.reason ?? result.summary;
    default:
      return result.summary;
  }
}

export function GlobalAssistantDock() {
  const dock = useAssistantDock();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { loading, error, submit, clearError } = useAssistantAction();

  const [input, setInput] = useState('');
  const [reply, setReply] = useState<DockReply | null>(null);
  // Recent conversation turns, so a reply to a clarification (or any follow-up) keeps context.
  const [turns, setTurns] = useState<AssistantTurn[]>([]);
  // Transient success banner for kind:'action' (shown briefly before/while navigating).
  const [actionSummary, setActionSummary] = useState<string | null>(null);
  // #4 — live interim transcript shown in the field WHILE dictating, before the final commit.
  const [partial, setPartial] = useState('');
  // #3 — keyboard height so the sheet lifts the input + Send above the Android soft keyboard.
  const [kbHeight, setKbHeight] = useState(0);

  // Seed the field when a screen opens the dock with prefilled text.
  useEffect(() => {
    if (dock?.open && dock.seedText) {
      setInput(dock.seedText);
      dock.consumeSeed();
    }
  }, [dock?.open, dock?.seedText, dock]);

  // Track the keyboard height so the bottom sheet can pad itself above the keyboard on Android
  // (a bottom-sheet Modal does not reliably get keyboard metrics from KeyboardAvoidingView there).
  useEffect(() => {
    const show = Keyboard.addListener('keyboardDidShow', (e) => setKbHeight(e.endCoordinates.height));
    const hide = Keyboard.addListener('keyboardDidHide', () => setKbHeight(0));
    return () => {
      show.remove();
      hide.remove();
    };
  }, []);

  if (!dock) return null;

  const { open, openDock, closeDock, placeholder, route } = dock;

  async function handleSubmit() {
    const text = composeInput(input, partial).trim();
    if (!text || loading) return;
    setReply(null);
    setActionSummary(null);
    setPartial('');
    // `turns` is the history BEFORE this message (setTurns is async) — send it for context.
    const result = await submit(text, { route, history: turns.slice(-10) });
    if (!result) return; // error rendered by the hook
    // Record this exchange so the next message (e.g. a reply to a clarification) has context.
    setTurns((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: assistantTurnContent(result) }]);
    if (result.kind === 'action') {
      // The hook already pushed the deep link. Surface the confirmation + close.
      setActionSummary(result.summary);
      setInput('');
      // Give the banner a beat, then collapse so the user lands on the entity screen.
      setTimeout(() => {
        setActionSummary(null);
        closeDock();
      }, 1400);
      return;
    }
    // answer / clarification / needs_owner → keep the dock open, show inline.
    const r = toReply(result);
    setReply(r);
    // Clarification expects a follow-up turn → clear the field for the next message.
    if (result.kind === 'clarification' || result.kind === 'answer') setInput('');
  }

  function handleClose() {
    closeDock();
    setReply(null);
    setActionSummary(null);
    setPartial('');
    setTurns([]); // start a fresh conversation next time the dock opens
    clearError();
  }

  function openFullAssistant() {
    handleClose();
    router.push('/(app)/assistant');
  }

  return (
    <>
      {/* Collapsed FAB/pill — hidden while the expanded sheet is open. */}
      {!open ? (
        <Pressable
          onPress={() => openDock()}
          accessibilityRole="button"
          accessibilityLabel="Ask BEAM"
          testID="assistant-dock-pill"
          style={({ pressed }) => [
            styles.fab,
            { bottom: insets.bottom + theme.spacing['4xl'] + theme.spacing.lg },
            pressed && styles.fabPressed,
          ]}
        >
          <Text variant="label" color="onPrimary" style={styles.fabIcon}>
            ✦
          </Text>
          <Text variant="label" color="onPrimary" numberOfLines={1}>
            {COLLAPSED_LABEL}
          </Text>
        </Pressable>
      ) : null}

      {/* Expanded input sheet. */}
      <Modal
        visible={open}
        transparent
        animationType="slide"
        onRequestClose={handleClose}
      >
        <KeyboardAvoidingView
          style={styles.modalRoot}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          {/* Scrim — tap to dismiss. */}
          <Pressable
            style={styles.scrim}
            accessibilityRole="button"
            accessibilityLabel="Close assistant"
            onPress={handleClose}
          />

          <View
            style={[
              styles.sheet,
              // Lift the sheet above the Android soft keyboard (see keyboard listener above).
              { paddingBottom: insets.bottom + theme.spacing.md + (Platform.OS === 'android' ? kbHeight : 0) },
            ]}
            testID="assistant-dock-sheet"
          >
            <View style={styles.sheetHeader}>
              <Text variant="subhead">Ask BEAM</Text>
              <Button
                title="Close"
                variant="ghost"
                size="sm"
                fullWidth={false}
                onPress={handleClose}
                accessibilityLabel="Close assistant"
              />
            </View>

            <Text variant="small" color="secondary">
              Describe what you want done. I'll route it to the right place.
            </Text>

            {/* Success banner (kind:'action'). */}
            {actionSummary ? (
              <View accessibilityRole="alert" testID="assistant-dock-success">
                <Card style={styles.successCard}>
                  <Text variant="small" color="primary">{actionSummary}</Text>
                  <Text variant="small" color="secondary">Opening it now…</Text>
                </Card>
              </View>
            ) : null}

            {/* Inline reply (answer / clarification / needs_owner). */}
            {reply?.kind === 'answer' ? (
              <View accessibilityRole="summary">
                <Card style={styles.replyCard}>
                  <Text variant="body" color="primary">{reply.content}</Text>
                  <Pressable
                    onPress={openFullAssistant}
                    accessibilityRole="button"
                    accessibilityLabel="Open the full assistant"
                    style={styles.linkRow}
                  >
                    <Text variant="small" color="link">Open full assistant →</Text>
                  </Pressable>
                </Card>
              </View>
            ) : null}

            {reply?.kind === 'clarification' ? (
              <View accessibilityRole="alert">
                <Card style={styles.clarifyCard}>
                  <Text variant="label" color="secondary">
                    {reply.progress ? `GATHERING DETAILS · STEP ${reply.progress.step} OF ${reply.progress.total}` : 'ONE MORE THING'}
                  </Text>
                  <Text variant="body" color="primary">{reply.clarification}</Text>
                </Card>
              </View>
            ) : null}

            {reply?.kind === 'needs_owner' ? (
              <View accessibilityRole="alert">
                <Card style={styles.ownerCard}>
                  <Text variant="label" color="secondary">NEEDS OWNER APPROVAL</Text>
                  <Text variant="body" color="primary">{reply.reason}</Text>
                  <Text variant="small" color="secondary">
                    An owner has to approve this before it can be applied.
                  </Text>
                </Card>
              </View>
            ) : null}

            {/* Error. */}
            {error ? (
              <View accessibilityRole="alert">
                <Card style={styles.errorCard}>
                  <Text variant="small" color="danger">{error}</Text>
                </Card>
              </View>
            ) : null}

            {/* Input row. */}
            <View style={styles.inputRow}>
              <Field
                value={composeInput(input, partial)}
                onChangeText={(t) => {
                  setInput(t);
                  if (partial) setPartial('');
                  if (error) clearError();
                }}
                placeholder={placeholder}
                multiline
                maxLength={2000}
                editable={!loading}
                returnKeyType="send"
                blurOnSubmit={false}
                onSubmitEditing={handleSubmit}
                autoFocus
                accessibilityLabel="Ask BEAM input"
                testID="assistant-dock-input"
                containerStyle={styles.fieldContainer}
                style={styles.fieldInput}
              />
              <MicButton
                onTranscribe={(t) => {
                  setInput((prev) => (prev.trim() ? `${prev} ${t}` : t));
                  setPartial('');
                }}
                onPartialTranscribe={setPartial}
                disabled={loading}
                label=""
                size="md"
                accessibilityLabel="Dictate to BEAM"
              />
              <Button
                title="Send"
                variant="primary"
                size="md"
                fullWidth={false}
                onPress={handleSubmit}
                loading={loading}
                disabled={!composeInput(input, partial).trim() || loading}
                accessibilityLabel="Send to BEAM"
                testID="assistant-dock-send"
              />
            </View>

            {loading ? (
              <View style={styles.loadingRow} accessibilityLabel="Working on it">
                <ActivityIndicator size="small" color={theme.color.text.secondary} />
                <Text variant="small" color="secondary">Working on it…</Text>
              </View>
            ) : null}
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  fab: {
    position: 'absolute',
    right: theme.layout.screenPadding,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-end',
    gap: theme.spacing.xs,
    backgroundColor: theme.color.action.primary,
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    borderRadius: theme.radius.pill,
    ...theme.shadow.card,
  },
  fabPressed: {
    opacity: 0.85,
  },
  fabIcon: {
    fontSize: theme.spacing.lg,
  },
  modalRoot: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  scrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: theme.color.scrim,
  },
  sheet: {
    backgroundColor: theme.color.bg.surface,
    borderTopLeftRadius: theme.radius.lg,
    borderTopRightRadius: theme.radius.lg,
    paddingHorizontal: theme.layout.screenPadding,
    paddingTop: theme.spacing.lg,
    gap: theme.spacing.sm,
  },
  sheetHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: theme.spacing.sm,
    paddingTop: theme.spacing.xs,
  },
  fieldContainer: {
    flex: 1,
  },
  fieldInput: {
    maxHeight: 120,
    textAlignVertical: 'top',
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    paddingTop: theme.spacing.xs,
  },
  successCard: {
    backgroundColor: theme.status.paid.bg,
    borderColor: theme.color.border.default,
    gap: theme.spacing.xs,
  },
  replyCard: {
    backgroundColor: theme.color.bg.subtle,
    borderColor: theme.color.border.default,
    gap: theme.spacing.sm,
  },
  clarifyCard: {
    backgroundColor: theme.status.inProgress.bg,
    borderColor: theme.color.border.default,
    gap: theme.spacing.xs,
  },
  ownerCard: {
    backgroundColor: theme.status.sent.bg,
    borderColor: theme.color.border.default,
    gap: theme.spacing.xs,
  },
  errorCard: {
    backgroundColor: theme.status.overdue.bg,
    borderColor: theme.color.action.danger,
  },
  linkRow: {
    paddingTop: theme.spacing.xs,
  },
});
